import { useEffect, useState } from 'react'
import { Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth, useUser } from '@clerk/clerk-react'
import { Loader2, RefreshCw } from 'lucide-react'
import { SUPPORT_EMAIL } from '@dealcloser/shared'
import { useApi } from '@/hooks/use-api'
import { canViewAdminConsole } from '@/features/admin/staff'

/**
 * The single gate that orders a customer's first-run journey:
 *
 *   signed in  →  ONBOARDING  →  PAYMENT  →  DASHBOARD
 *
 * (Product decision 2026-06-12: onboarding runs BEFORE the payment wall.)
 *
 * Both stages are driven by server truth from /api/auth/me:
 *   - onboarding_completed === 1  → onboarding done
 *   - entitled === true           → comped OR has a live Stripe subscription
 *
 * Staff / comp accounts have entitled=true, so they skip the payment wall
 * automatically — no email allowlist needed here anymore. The old
 * ProtectedRoute admin-email gate is gone; this guard is the only gate.
 */

type UserData = {
  user: {
    plan_status: string
    onboarding_completed: number
    entitled?: boolean
  }
}

// How long to wait for the Stripe webhook to flip `entitled` after the user
// returns from Checkout. Stripe usually delivers within seconds, but the
// webhook worker can lag (cold start, retries), so the window is generous:
// ~120 s, polling every 3 s for the first 15 s and every 5 s after that.
// Past the window we do NOT bounce to /billing (that re-showed the payment
// wall to someone who had just paid) — we offer a Retry that restarts polling.
const POST_CHECKOUT_WINDOW_MS = 120_000
const POST_CHECKOUT_FAST_MS = 3_000
const POST_CHECKOUT_SLOW_MS = 5_000
const POST_CHECKOUT_FAST_UNTIL_MS = 15_000

export function AccessGuard({ children }: { readonly children: React.ReactNode }) {
  const { isSignedIn } = useAuth()
  const { user: clerkUser } = useUser()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const api = useApi()

  // DCA staff and QA always skip both gates (onboarding + payment), whatever
  // the D1 row says, so a missing/uncomped row can never lock a team member
  // out. Matched by Clerk user id (staff.ts, mirrors the server allowlists):
  // the old email list shipped staff addresses in the public bundle (launch
  // audit 2026-10-24). Customer and test accounts go through the real flow.
  const isStaff = canViewAdminConsole(clerkUser?.id)

  // Just back from Stripe Checkout? The subscription webhook may not have
  // landed yet, so we poll /auth/me instead of bouncing them to /billing.
  const justPaid = searchParams.get('checkout') === 'success'
  // Elapsed polling time in ms; `polling` false means the window ran out.
  const [elapsedMs, setElapsedMs] = useState(0)
  const [polling, setPolling] = useState(true)
  const pollDelay = elapsedMs < POST_CHECKOUT_FAST_UNTIL_MS ? POST_CHECKOUT_FAST_MS : POST_CHECKOUT_SLOW_MS

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['user-me'],
    queryFn: () => api<UserData>('/api/auth/me'),
    enabled: !!isSignedIn,
    staleTime: justPaid ? 0 : 5_000,
    refetchInterval: (query) => {
      if (!justPaid || !polling) return false
      // Stop as soon as the row says entitled — no point hammering /auth/me.
      if (query.state.data?.data?.user?.entitled === true) return false
      return pollDelay
    },
  })

  const entitledNow = data?.data?.user?.entitled === true
  const stillWaiting = justPaid && polling && !entitledNow

  useEffect(() => {
    if (!stillWaiting) return
    const t = setTimeout(() => {
      setElapsedMs((e) => {
        const next = e + pollDelay
        if (next >= POST_CHECKOUT_WINDOW_MS) setPolling(false)
        return next
      })
    }, pollDelay)
    return () => clearTimeout(t)
  }, [stillWaiting, pollDelay, elapsedMs])

  function restartPolling() {
    setElapsedMs(0)
    setPolling(true)
    void refetch()
  }

  // Staff bypass — never depends on the /auth/me row existing (a staff
  // account with no users row which would otherwise spin forever on a 404).
  if (isStaff) {
    return <>{children}</>
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const path = location.pathname

  // No row yet (user.created webhook still in flight right after signup) or
  // /auth/me failed — default a non-staff session into onboarding rather than
  // hanging on a spinner. The row will exist by the time the wizard needs it.
  const user = data?.data?.user
  if (!user) {
    return path === '/onboarding' ? <>{children}</> : <Navigate to="/onboarding" replace />
  }
  const onboarded = user.onboarding_completed === 1
  const entitled = user.entitled === true

  // Post-checkout grace window: show a "confirming payment" screen while we
  // wait for the webhook, rather than flashing the payment wall again.
  if (justPaid && !entitled) {
    if (polling) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-center px-4">
          <Loader2 className="h-8 w-8 animate-spin text-cobalt-bright" />
          <p className="text-sm font-medium">Confirming your payment…</p>
          <p className="text-sm text-muted-foreground">This can take a minute. Please keep this page open.</p>
        </div>
      )
    }
    // Window elapsed and still not entitled. Stay here (never bounce a paid
    // user back to the payment wall) and let them restart the wait.
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-center px-4">
        <p className="text-sm font-medium">Still confirming your payment</p>
        <p className="max-w-sm text-sm text-muted-foreground">
          Stripe has your payment but our confirmation is taking longer than usual. Retry below, or
          email {SUPPORT_EMAIL} if this keeps happening — you will not be charged twice.
        </p>
        <button
          type="button"
          onClick={restartPolling}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <RefreshCw className="h-4 w-4" />
          Retry
        </button>
      </div>
    )
  }

  // ----- Stage 1: onboarding -----
  if (!onboarded) {
    // Force every route to /onboarding until it's finished.
    return path === '/onboarding' ? <>{children}</> : <Navigate to="/onboarding" replace />
  }
  // Onboarding done but still sitting on /onboarding → push forward.
  if (path === '/onboarding') {
    return <Navigate to={entitled ? '/dashboard' : '/billing'} replace />
  }

  // ----- Stage 2: payment -----
  if (!entitled) {
    // Onboarding finished, no active subscription → force the payment wall.
    return path === '/billing' ? <>{children}</> : <Navigate to="/billing" replace />
  }
  // Entitled but landed on /billing (e.g. back button after paying) → home.
  if (path === '/billing') {
    return <Navigate to="/dashboard" replace />
  }

  // ----- Cleared both gates -----
  return <>{children}</>
}
